import React, { useCallback, useImperativeHandle, useState, useMemo } from "react";
import { Svg } from "@whatslly/ui-components/v2/BaseElements/Svg";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import classnames from "classnames";
import styles from "./multiple-Input-select.module.scss";
import { Text } from "@whatslly/ui-components/Text";

export type MultipleInputSelectRef = { addItemToArray: (value: string) => void; clear: VoidFunction };

export const MultipleInputSelect = React.forwardRef<
  MultipleInputSelectRef,
  {
    prefix?: string;
    items: string[] | undefined;
    labelText?: string;
    disabled?: boolean;
    required?: boolean;
    className?: string;
    onChange: (items: string[]) => void;
    isValidFn?: (data: string) => boolean;
    itemType: string;
    showError?: boolean;
    errorMessageClassName?: string;
    onInputChange?: (value: string) => void;
    testId?: string;
    autoFocus?: boolean;
    maxSelectedItems?: number;
    onlyUniqueItems?: boolean;
  }
>(
  (
    {
      prefix,
      items,
      labelText,
      required,
      disabled,
      className,
      onChange,
      isValidFn,
      itemType,
      showError,
      errorMessageClassName,
      testId,
      onInputChange,
      autoFocus,
      maxSelectedItems,
      onlyUniqueItems,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState<string>("");
    const [backspaceCounter, setBackspaceCounter] = useState<number>(0);
    const [errorType, setErrorType] = useState<"invalid" | "duplicate" | null>(null);
    const isMaxItemsReached = useMemo(() => maxSelectedItems !== undefined && items && items.length >= maxSelectedItems, [maxSelectedItems, items]);
    const normalizedItems = useMemo(() => items?.map((item) => item.toLowerCase()) || [], [items]);

    const processItem = useCallback(
      (value: string): { valid: boolean; value: string } => {
        const trimmedValue = value.trim();

        if (!trimmedValue) {
          return { valid: false, value: "" };
        } else if (onlyUniqueItems && normalizedItems.includes(trimmedValue.toLowerCase())) {
          setErrorType("duplicate");
          return { valid: false, value: trimmedValue };
        } else if (isValidFn && !isValidFn(trimmedValue)) {
          setErrorType("invalid");
          return { valid: false, value: trimmedValue };
        }

        return { valid: true, value: trimmedValue };
      },
      [onlyUniqueItems, normalizedItems, isValidFn]
    );

    const addItem = useCallback(
      (value: string): boolean => {
        const result = processItem(value);
        if (result.valid) {
          onChange([...(items || []), result.value]);
          setInputValue("");
          setErrorType(null);
          return true;
        }
        return false;
      },
      [items, onChange, processItem]
    );

    const addItems = useCallback(
      (values: string[]): boolean => {
        if (!values.length) return false;

        if (maxSelectedItems !== undefined && items && items.length + values.length > maxSelectedItems) {
          return false;
        }

        const validItems: string[] = [];
        let hasError = false;

        for (const value of values) {
          const result = processItem(value);
          if (result.valid) {
            validItems.push(result.value);
          } else {
            hasError = true;
          }
        }

        if (validItems.length) {
          onChange([...(items || []), ...validItems]);
          setInputValue("");
          return true;
        }

        return !hasError;
      },
      [items, onChange, processItem, maxSelectedItems]
    );

    const handleInputChange = useCallback(
      (value: string) => {
        onInputChange?.(value);
        setInputValue(value);
        setErrorType(null);
      },
      [onInputChange]
    );

    useImperativeHandle(ref, () => ({
      addItemToArray: (value: string) => {
        addItem(value);
      },
      clear: () => {
        onChange([]);
        setInputValue("");
        setErrorType(null);
      },
    }));

    const removeItem = useCallback(
      (text: string) => {
        const tempArrayOfItem = [...(items ?? [])];
        const index = tempArrayOfItem.indexOf(text);
        if (index > -1) {
          tempArrayOfItem.splice(index, 1);
        }
        onChange(tempArrayOfItem);
        setErrorType(null);
      },
      [items, onChange]
    );

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === ",") {
          event.stopPropagation();
          event.preventDefault();
        }

        if (event.key === "Enter" || event.key === ",") {
          addItem(inputValue);
          return;
        }

        if (inputValue === "" && event.key === "Backspace") {
          if (backspaceCounter !== 1) {
            setBackspaceCounter((count) => count + 1);
          }
          if (items && backspaceCounter === 1 && items.length > 0) {
            setBackspaceCounter(0);
            const lastItem = items[items.length - 1];
            removeItem(lastItem);
          }
          return;
        }
        setBackspaceCounter(0);
        setErrorType(null);
      },
      [inputValue, backspaceCounter, removeItem, addItem, items]
    );

    const handleBlurChange = useCallback(() => {
      if (inputValue.trim()) {
        addItem(inputValue);
      }
    }, [inputValue, addItem]);

    const handleOnPaste = useCallback(
      (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.stopPropagation();
        event.preventDefault();
        const value = event.clipboardData.getData(`text`);
        if (!value) return;
        if (!value.includes(",")) {
          setInputValue(value);
          return;
        }
        const valueParts = value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);

        addItems(valueParts);
      },
      [addItems]
    );

    const SingleItem: React.FC<{ text: string }> = ({ text }) => {
      return (
        <div className={styles.singleInputItem} data-testid={`${prefix}-single-item`}>
          <div className={styles.singleInputText}>{text}</div>
          <Svg
            iconName="cancel-actions"
            width={10}
            height={10}
            className={classnames(styles.singleInputItemDeleteIcon)}
            onClick={() => removeItem(text)}
          />
        </div>
      );
    };

    const errorMessage = useMemo(() => {
      if (!showError || !errorType) return null;

      switch (errorType) {
        case "invalid":
          return `Invalid ${itemType.toLowerCase()} format`;
        case "duplicate":
          return `This ${itemType.toLowerCase()} has already been added`;
        default:
          return null;
      }
    }, [errorType, showError, itemType, maxSelectedItems]);

    return (
      <>
        {labelText && <Label text={labelText} required={required ?? false} />}
        <div className={classnames(styles.toInputContainer, className)}>
          {items?.map((item, index) => {
            return <SingleItem key={index + item} text={item} />;
          })}
          <input
            disabled={disabled || isMaxItemsReached}
            data-testid={testId}
            autoFocus={autoFocus === undefined ? true : autoFocus}
            value={inputValue}
            onKeyDown={handleKeyDown}
            onBlur={handleBlurChange}
            onChange={(e) => handleInputChange(e.target.value)}
            className={styles.toInput}
            type={"text"}
            onPaste={handleOnPaste}
            placeholder={isMaxItemsReached ? `Maximum of ${maxSelectedItems} items reached` : undefined}
          />
        </div>
        {errorMessage && (
          <Text skin="error" className={errorMessageClassName}>
            {errorMessage}
          </Text>
        )}
      </>
    );
  }
);
